import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  Wallet,
  TrendingUp,
  TrendingDown,
  ArrowLeftRight,
  Search,
  RefreshCw,
  Pencil,
  Trash2,
  Download,
  CalendarDays,
  HandCoins,
  CircleDollarSign,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const TYPES = [
  { value: "income", label: "Income" },
  { value: "expense", label: "Expense" },
  // { value: "transfer", label: "Cash / Bank Transfer" },
  // { value: "capital", label: "Owner Capital" },
  // { value: "loan_received", label: "Loan Received" },
  // { value: "loan_repayment", label: "Loan Repayment" },
];

const CATEGORIES = {
  income: [
    "Internet Subscription",
    "Installation Fee",
    "Reconnection Fee",
    "Other Income",
    "Others",
  ],
  expense: [
    "Internet Bandwidth",
    "Electricity",
    "Salary / Payroll",
    "Fuel / Transportation",
    "Equipment",
    "Maintenance / Repair",
    "Office Supplies",
    "Rent",
    "Marketing",
    "Others",
  ],
  transfer: [
    "Cash to Bank",
    "Bank to Cash",
    "Bank to Bank",
    "E-wallet Transfer",
    "Others",
  ],
  capital: ["Owner Investment", "Additional Capital", "Others"],
  loan_received: ["Business Loan", "Personal Loan for Business", "Others"],
  loan_repayment: ["Loan Principal", "Loan Interest", "Others"],
};

const EMPTY_FORM = {
  transaction_date: new Date().toLocaleDateString("en-CA"),
  transaction_type: "income",
  category: "Internet Subscription",
  custom_category: "",
  description: "",
  amount: "",
  payment_method: "Cash",
  reference_number: "",
  contact_name: "",
  notes: "",
  status: "completed",
};

const peso = (value) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const dateLabel = (value) => {
  if (!value) return "—";
  const raw = String(value).slice(0, 10);
  const parts = raw.split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    return new Date(
      Number(parts[0]),
      Number(parts[1]) - 1,
      Number(parts[2]),
    ).toLocaleDateString("en-PH", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? String(value)
    : parsed.toLocaleDateString("en-PH");
};

const typeLabel = (type) =>
  TYPES.find((item) => item.value === type)?.label || type || "—";

export default function AdminCashFlow() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [form, setForm] = useState(EMPTY_FORM);

  const token = () => localStorage.getItem("token");
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: pageSize,
      };

      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (typeFilter !== "all") {
        params.transaction_type = typeFilter;
      }
      if (search.trim()) params.search = search.trim();

      const response = await axios.get(`${API}/cashflow`, {
        params,
        headers: authHeaders(),
      });

      const data = response.data;

      setTransactions(
        Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
            ? data
            : [],
      );

      setTotalRecords(Number(data?.total_completed) || 0);

      setTotalPages(Number(data?.pages) || 1);
    } catch (error) {
      setTransactions([]);
      toast.error(
        error.response?.data?.detail || "Failed to load cash flow records",
      );
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, typeFilter, search, currentPage, pageSize]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, typeFilter, startDate, endDate, pageSize]);

  const summary = useMemo(() => {
    return transactions.reduce(
      (totals, item) => {
        if (item.status !== "completed") return totals;
        const amount = Number(item.amount) || 0;
        switch (item.transaction_type) {
          case "income":
            totals.income += amount;
            break;
          case "expense":
          case "loan_repayment":
            totals.expenses += amount;
            break;
          case "capital":
            totals.capital += amount;
            break;
          case "loan_received":
            totals.loans += amount;
            break;
          case "transfer":
            totals.transfers += amount;
            break;
          default:
            break;
        }
        return totals;
      },
      { income: 0, expenses: 0, capital: 0, loans: 0, transfers: 0 },
    );
  }, [transactions]);

  const netOperatingCashFlow = summary.income - summary.expenses;

  const setField = (field, value) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === "transaction_type") {
        const nextCategories = CATEGORIES[value] || ["Others"];
        next.category = nextCategories[0];
        next.custom_category = "";
      }
      return next;
    });
  };

  const openAddDialog = () => {
    setEditingTransaction(null);
    setForm({
      ...EMPTY_FORM,
      transaction_date: new Date().toLocaleDateString("en-CA"),
    });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingTransaction(item);
    const categoryList = CATEGORIES[item.transaction_type] || ["Others"];
    const category = item.category || "Others";
    const isCustomCategory = !categoryList.includes(category);
    setForm({
      ...EMPTY_FORM,
      ...item,
      transaction_date:
        String(item.transaction_date || "").slice(0, 10) ||
        EMPTY_FORM.transaction_date,
      transaction_type: item.transaction_type || "income",
      category: isCustomCategory ? "Others" : category,
      custom_category:
        item.custom_category || (isCustomCategory ? category : ""),
      amount:
        item.amount === undefined || item.amount === null
          ? ""
          : String(item.amount),
      status: item.status || "completed",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const amount = Number(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter an amount greater than zero");
      return;
    }
    if (form.category === "Others" && !form.custom_category.trim()) {
      toast.error("Please specify the custom category");
      return;
    }

    const payload = {
      ...form,
      amount,
      category:
        form.category === "Others"
          ? form.custom_category.trim()
          : form.category,
      custom_category:
        form.category === "Others" ? form.custom_category.trim() : "",
      description: form.description.trim(),
      reference_number: form.reference_number.trim(),
      contact_name: form.contact_name.trim(),
      notes: form.notes.trim(),
    };

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      if (editingTransaction) {
        const id = editingTransaction.id || editingTransaction._id;
        await axios.put(`${API}/cashflow/${id}`, payload, {
          headers: authHeaders(),
        });
        toast.success("Cash flow record updated");
      } else {
        await axios.post(`${API}/cashflow`, payload, {
          headers: authHeaders(),
        });
        toast.success("Cash flow record added");
      }
      setDialogOpen(false);
      setEditingTransaction(null);
      setForm(EMPTY_FORM);
      await fetchTransactions();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to save cash flow record",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    const id = item.id || item._id;
    if (!id) {
      toast.error("This record has no ID and cannot be deleted");
      return;
    }
    if (
      !window.confirm(
        `Delete this ${typeLabel(item.transaction_type).toLowerCase()} record?`,
      )
    )
      return;
    try {
      await axios.delete(`${API}/cashflow/${id}`, { headers: authHeaders() });
      toast.success("Cash flow record deleted");
      await fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete record");
    }
  };

  const exportCsv = () => {
    const columns = [
      ["Date", "transaction_date"],
      ["Type", "transaction_type"],
      ["Category", "category"],
      ["Description", "description"],
      ["Amount", "amount"],
      ["Payment Method", "payment_method"],
      ["Reference", "reference_number"],
      ["Contact", "contact_name"],
      ["Status", "status"],
      ["Notes", "notes"],
    ];
    const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const rows = [
      columns.map(([label]) => escapeCsv(label)).join(","),
      ...transactions.map((item) =>
        columns
          .map(([, key]) =>
            escapeCsv(
              key === "transaction_type" ? typeLabel(item[key]) : item[key],
            ),
          )
          .join(","),
      ),
    ];
    const blob = new Blob(["\uFEFF" + rows.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `cash-flow-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const visibleTotal = transactions.reduce(
    (total, item) =>
      total +
      (item.status === "void" || item.status === "cancelled"
        ? 0
        : Number(item.amount) || 0),
    0,
  );

  const selectedCategories = CATEGORIES[form.transaction_type] || ["Others"];

  return (
    <AdminLayout>
      <div className="min-h-[calc(100vh-64px)] p-4 sm:p-6 lg:p-8 space-y-6 bg-slate-50/60">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-blue-100 p-2.5 text-blue-700">
                <Wallet className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                  Cash Flow
                </h1>
                <p className="mt-1 text-sm text-slate-600">
                  {/* Track business income, expenses, capital, loans, and
                  transfers. */}
                  Track business income, expenses. ( Alwasy filter the date
                  range. )
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Refresh Button */}
            <Button
              onClick={fetchTransactions}
              disabled={loading}
              className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-lg shadow-blue-600/30 gap-2"
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={!transactions.length}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={openAddDialog}
                  className="gap-2 bg-blue-600 text-white hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4" />
                  Add Transaction
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
                  <DialogTitle className="text-xl font-bold">
                    {editingTransaction
                      ? "Edit Cash Flow Transaction"
                      : "Add Cash Flow Transaction"}
                  </DialogTitle>
                </DialogHeader>
                <div className="px-6 pb-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="transaction_date">
                          Transaction Date *
                        </Label>
                        <Input
                          id="transaction_date"
                          type="date"
                          value={form.transaction_date}
                          onChange={(e) =>
                            setField("transaction_date", e.target.value)
                          }
                          required
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="transaction_type">
                          Transaction Type *
                        </Label>
                        <select
                          id="transaction_type"
                          value={form.transaction_type}
                          onChange={(e) =>
                            setField("transaction_type", e.target.value)
                          }
                          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                          required
                        >
                          {TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="category">Category *</Label>
                        <select
                          id="category"
                          value={form.category}
                          onChange={(e) => setField("category", e.target.value)}
                          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                          required
                        >
                          {selectedCategories.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </div>
                      {form.category === "Others" && (
                        <div className="space-y-1.5">
                          <Label htmlFor="custom_category">
                            Specify Category *
                          </Label>
                          <Input
                            id="custom_category"
                            value={form.custom_category}
                            onChange={(e) =>
                              setField("custom_category", e.target.value)
                            }
                            placeholder="e.g. emergency repair, permits..."
                            required
                          />
                        </div>
                      )}
                      <div className="space-y-1.5">
                        <Label htmlFor="amount">Amount (₱) *</Label>
                        <Input
                          id="amount"
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={form.amount}
                          onChange={(e) => setField("amount", e.target.value)}
                          placeholder="0.00"
                          required
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="payment_method">Payment Method</Label>
                        <select
                          id="payment_method"
                          value={form.payment_method}
                          onChange={(e) =>
                            setField("payment_method", e.target.value)
                          }
                          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                        >
                          {[
                            "Cash",
                            "Bank Transfer",
                            "GCash",
                            "Maya",
                            "Cheque",
                            "Credit Card",
                            "Other",
                          ].map((method) => (
                            <option key={method} value={method}>
                              {method}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="description">Description *</Label>
                        <Input
                          id="description"
                          value={form.description}
                          onChange={(e) =>
                            setField("description", e.target.value)
                          }
                          placeholder="What is this transaction for?"
                          required
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="contact_name">
                          Paid To / Received From
                        </Label>
                        <Input
                          id="contact_name"
                          value={form.contact_name}
                          onChange={(e) =>
                            setField("contact_name", e.target.value)
                          }
                          placeholder="Person, supplier, or customer"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="reference_number">
                          Reference / OR Number
                        </Label>
                        <Input
                          id="reference_number"
                          value={form.reference_number}
                          onChange={(e) =>
                            setField("reference_number", e.target.value)
                          }
                          placeholder="Optional reference number"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="status">Record Status</Label>
                        <select
                          id="status"
                          value={form.status}
                          onChange={(e) => setField("status", e.target.value)}
                          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                        >
                          <option value="completed">Completed</option>
                          <option value="pending">Pending</option>
                          <option value="cancelled">Void / Cancelled</option>
                        </select>
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label htmlFor="notes">Notes</Label>
                        <textarea
                          id="notes"
                          value={form.notes}
                          onChange={(e) => setField("notes", e.target.value)}
                          rows={3}
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          placeholder="Additional details, optional..."
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 border-t pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setDialogOpen(false)}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={saving}
                        className="bg-blue-600 text-white hover:bg-blue-700"
                      >
                        {saving
                          ? "Saving..."
                          : editingTransaction
                            ? "Save Changes"
                            : "Save Transaction"}
                      </Button>
                    </div>
                  </form>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <SummaryCard
            title="Total Income"
            value={peso(summary.income)}
            description="Operating income recorded"
            icon={<TrendingUp className="h-5 w-5" />}
            iconClass="bg-emerald-100 text-emerald-700"
          />
          <SummaryCard
            title="Total Expenses"
            value={peso(summary.expenses)}
            description="Expenses + loan repayments"
            icon={<TrendingDown className="h-5 w-5" />}
            iconClass="bg-rose-100 text-rose-700"
          />
          <SummaryCard
            title="Net Operating Cash Flow"
            value={peso(netOperatingCashFlow)}
            description="Income minus expenses"
            icon={<CircleDollarSign className="h-5 w-5" />}
            iconClass={
              netOperatingCashFlow >= 0
                ? "bg-blue-100 text-blue-700"
                : "bg-amber-100 text-amber-700"
            }
            valueClass={
              netOperatingCashFlow >= 0 ? "text-slate-900" : "text-rose-700"
            }
          />
          {/* <SummaryCard
            title="Capital + Loans"
            value={peso(summary.capital + summary.loans)}
            description={`${peso(summary.capital)} capital · ${peso(summary.loans)} loans received`}
            icon={<HandCoins className="h-5 w-5" />}
            iconClass="bg-violet-100 text-violet-700"
          /> */}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="font-semibold text-slate-900">Transactions</h2>
              <p className="text-sm text-slate-500">
                Showing {transactions.length} record(s) · Visible amount total:{" "}
                {peso(visibleTotal)}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="relative mt-7">
                <Search className="absolute left-3 top-[19px] h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  placeholder="Search transactions..."
                />
              </div>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm mt-7"
              >
                <option value="all">All transaction types</option>
                {TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">From</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">To</Label>
                <Input
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[950px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-y border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Type / Category</th>
                  <th className="px-4 py-3 font-semibold">Description</th>
                  <th className="px-4 py-3 font-semibold">
                    Method / Reference
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">Amount</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-12 text-center text-slate-500"
                    >
                      <RefreshCw className="mr-2 inline h-4 w-4 animate-spin" />
                      Loading cash flow records...
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-12 text-center text-slate-500"
                    >
                      <Wallet className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                      <p className="font-medium text-slate-700">
                        No transactions found
                      </p>
                      <p className="mt-1 text-xs">
                        Add a record or adjust your filters.
                      </p>
                    </td>
                  </tr>
                ) : (
                  transactions.map((item) => {
                    const amount = Number(item.amount) || 0;
                    const isOutflow = ["expense", "loan_repayment"].includes(
                      item.transaction_type,
                    );
                    const isExcluded = ["void", "cancelled"].includes(
                      item.status,
                    );
                    return (
                      <tr
                        key={
                          item.id ||
                          item._id ||
                          `${item.transaction_date}-${item.description}`
                        }
                        className={`border-b border-slate-100 hover:bg-slate-50/80 ${isExcluded ? "opacity-50" : ""}`}
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                          {dateLabel(item.transaction_date)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-800">
                            {typeLabel(item.transaction_type)}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-500">
                            {item.category || "Uncategorized"}
                          </div>
                        </td>
                        <td className="max-w-[240px] px-4 py-3">
                          <div
                            className="truncate font-medium text-slate-800"
                            title={item.description}
                          >
                            {item.description || "—"}
                          </div>
                          {item.contact_name && (
                            <div className="mt-0.5 truncate text-xs text-slate-500">
                              {item.contact_name}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-700">
                            {item.payment_method || "—"}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-500">
                            {item.reference_number || "No reference"}
                          </div>
                        </td>
                        <td
                          className={`whitespace-nowrap px-4 py-3 text-right font-semibold ${isOutflow ? "text-rose-600" : item.transaction_type === "income" ? "text-emerald-700" : "text-slate-800"}`}
                        >
                          {isOutflow
                            ? "− "
                            : item.transaction_type === "income"
                              ? "+ "
                              : ""}
                          {peso(amount)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${item.status === "completed" || !item.status ? "border-emerald-200 bg-emerald-50 text-emerald-700" : item.status === "pending" ? "border-amber-200 bg-amber-50 text-amber-700" : "border-slate-200 bg-slate-100 text-slate-500"}`}
                          >
                            {item.status || "completed"}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(item)}
                            title="Edit transaction"
                            aria-label="Edit transaction"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(item)}
                            title="Delete transaction"
                            aria-label="Delete transaction"
                            className="text-rose-600 hover:text-rose-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
              <span>
                {totalRecords === 0
                  ? "Showing 0 records"
                  : `Showing ${(currentPage - 1) * pageSize + 1}–${Math.min(
                      currentPage * pageSize,
                      totalRecords,
                    )} of ${totalRecords}`}
              </span>

              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="h-9 rounded-md border border-input bg-white px-2"
                aria-label="Records per page"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={150}>150 per page</option>
                <option value={200}>200 per page</option>
                <option value={250}>250 per page</option>
                <option value={300}>300 per page</option>
                <option value={350}>350 per page</option>
                <option value={400}>400 per page</option>
                <option value={450}>450 per page</option>
                <option value={500}>500 per page</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={loading || currentPage <= 1}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              >
                Previous
              </Button>

              <span className="min-w-20 text-center text-sm text-slate-600">
                Page {currentPage} of {totalPages}
              </span>

              <Button
                variant="outline"
                size="sm"
                disabled={loading || currentPage >= totalPages}
                onClick={() =>
                  setCurrentPage((page) => Math.min(totalPages, page + 1))
                }
              >
                Next
              </Button>
            </div>
          </div>

          {/* <div className="mt-4 flex flex-col gap-1 border-t pt-3 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
            <span>
              <CalendarDays className="mr-1 inline h-3.5 w-3.5" />
              Summary reflects the records returned by the current filters.
            </span>
            <span>
              <ArrowLeftRight className="mr-1 inline h-3.5 w-3.5" />
              Transfers are tracked separately and excluded from income/expense
              totals.
            </span>
          </div> */}
        </div>
      </div>
    </AdminLayout>
  );
}

function SummaryCard({
  title,
  value,
  description,
  icon,
  iconClass,
  valueClass = "text-slate-900",
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className={`mt-2 text-2xl font-bold tracking-tight ${valueClass}`}>
            {value}
          </p>
          <p className="mt-1 text-xs text-slate-500">{description}</p>
        </div>
        <div className={`rounded-lg p-2.5 ${iconClass}`}>{icon}</div>
      </div>
    </div>
  );
}
